'use client';

import { useCallback } from 'react';
import { UIMarket } from '@drift/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useDriftClient from 'src/hooks/useDriftClient';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';

/**
 * Hook for updating funding rates for perpetual markets
 */
export const useUpdateFundingRate = () => {
	const driftClient = useDriftClient();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const getTxParams = useGetTxParams();

	const updateFundingRate = useCallback(
		async (perpMarketIndex: number) => {
			if (!driftClient || !walletAdapter) return false;
			const market = UIMarket.createPerpMarket(perpMarketIndex);

			const toastId = `updatingFundingRate_${perpMarketIndex}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				const txSig = await driftClient.updateFundingRate(
					perpMarketIndex,
					market.market.oracle,
					getTxParams()
				);

				if (txSig) {
					notify({
						id: toastId,
						type: 'success',
						message: 'Updated.',
						showUntilCancelled: false,
					});
					return true;
				} else {
					notify({
						id: toastId,
						type: 'error',
						message: 'There was an error updating funding, please try again.',
						showUntilCancelled: false,
					});
					return false;
				}
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
				return false;
			}
		},
		[driftClient, walletAdapter, getTxParams]
	);

	return { updateFundingRate };
};
